import { TabItem } from "elements/TabsSymphony";
import Colors from "modules/Colors";
import { DefaultStyles, WEBSITE_TABS, WebsiteLink, WebsiteLinks } from "modules/Types";

export const WEBSITE_VIEW_ONLY_TABS: TabItem[] = [
    {
        id: WEBSITE_TABS.ANALYTICS,
        title: 'Analytics',
        isProOnly: false,
    },
];

export const WEBSITE_EDITOR_TABS: TabItem[] = [
    {
        id: WEBSITE_TABS.SETUP,
        title: 'Setup',
        isProOnly: false,
    },
    {
        id: WEBSITE_TABS.STYLE,
        title: 'Style',
        isProOnly: false,
    },
    {
        id: WEBSITE_TABS.CONNECT,
        title: 'Connect',
        isProOnly: true,
    },
    {
        id: WEBSITE_TABS.ANALYTICS,
        title: 'Analytics',
        isProOnly: false,
    },
]

export const streamLinkOrientations = {
    boxes: {
        title: "Boxes",
        name: "boxes",
        order: 1,
    },
    stacked: {
        title: "Stacked",
        name: "stacked",
        order: 2,
    },
    traditional: {
        title: "Traditional",
        name: "traditional",
        order: 3,
    },
}




export const websiteSubSectionContainer = {
    backgroundColor: '#F0F0EF',
    borderRadius: 8,
    padding: 24,
    flexDirection: 'column',
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    alignItems: 'center',

} as const;

export const tourDateListContainer = {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
} as const;

export const websiteInputRow = {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
} as const;

export const tourDateRow = {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    border: `1px solid ${Colors.PAPER_DIVIDER_CONTAINER}`,
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
} as const;

export const tourDateDetail = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.purpleLightest,
    height: 56,
    width: 56,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
} as const;

export const tourDateDay = {
    fontSize: 18,
    color: Colors.textColor,
    marginBottom: -4,
} as const;

export const tourDateMonth = {
    fontSize: 16,
    color: Colors.textColor,
} as const;

export const tourDateAddUploadContainer = {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
} as const;


export const defaultIconProps: WebsiteLink = {
    enabled: true,
    rounded: true,
    backgroundColor: "",
    height: "30",
    width: "30",
    url: "",
    key: "",
    title: "",
    placeholder: "",
};

export const getDefaultStyles = (
    recordSelected: any,
    brandDefaultStyles: DefaultStyles,
) => {
    const styleObject = {
        bgType: 'artwork',
        bgValue: {
            color: '#0B0113',
            artwork: 0,
        },
        mainColor: Colors.purple,
        secondaryColor: Colors.textColor,
        styleType: 'boxes',
        themeStyle: 'vinyl',
        titleText: 'Listen To',
        subtitle: '',
        youtubeEmbed: {
            id: '',
            url: '',
        },
        removeSymphonyBrand: false,
        enableSocialIcons: false,
        socialIcons: [],
    } as DefaultStyles;

    if (recordSelected) {
        // these are styles set directly on the piece of content,
        // either from the user directly, or from the user's presave styles
        // that they set when creating their presave
        const { website: contentSpecificStyles, presave: presaveStyles } = recordSelected;

        // these are default styles that are set for the brand.

        // start by defaulting to the style saved within the
        // record directly
        let primaryStyle;
        if (contentSpecificStyles && contentSpecificStyles.style) {
            primaryStyle = contentSpecificStyles.style;
        } else if (presaveStyles && presaveStyles.style) {
            primaryStyle = presaveStyles.style;
        } else if (brandDefaultStyles) {
            primaryStyle = brandDefaultStyles;
        }

        if (primaryStyle) {
            // remove symphony brand
            if (brandDefaultStyles && brandDefaultStyles.removeSymphonyBrand) {
                styleObject.removeSymphonyBrand = brandDefaultStyles.removeSymphonyBrand;
            }

            if (brandDefaultStyles && brandDefaultStyles.enableSocialIcons) {
                styleObject.enableSocialIcons = brandDefaultStyles.enableSocialIcons;
            }

            // youtube embed
            if (primaryStyle.youtubeEmbed) {
                styleObject.youtubeEmbed = {
                    id: primaryStyle.youtubeEmbed.id,
                    url: primaryStyle.youtubeEmbed.url,
                };
            }

            styleObject.bgType = primaryStyle.bgType ?? 'artwork';

            if (primaryStyle.bgColor) {
                styleObject.bgValue = {
                    color: primaryStyle.bgColor,
                    artwork: 0,
                };
            }

            styleObject.styleType = primaryStyle.buttonConfig ?? 'boxes';
            styleObject.mainColor = primaryStyle.primaryColor ?? Colors.purple;
            styleObject.secondaryColor = primaryStyle.secondaryColor ?? Colors.textColor;
            styleObject.titleText = primaryStyle.titleText ?? 'Listen To';
            styleObject.subtitle = primaryStyle.subtitle ?? '';
        }
    }

    return styleObject;
};

export const getIconBackgroundStyle = (sm?: WebsiteLink) => {
    if (!sm) return;
    switch (sm.key) {
        case 'facebook':
            return { backgroundColor: 'rgba(25, 119, 243, 0.16)' };
        case 'instagram':
            return { background: 'radial-gradient(122.91% 144.6% at 1.81% 90.83%, rgba(254, 213, 118, 0.16) 0%, rgba(244, 113, 51, 0.16) 26.34%, rgba(188, 48, 129, 0.16) 60.91%, rgba(76, 99, 210, 0.16) 100%)' };
        case 'tiktok':
            return { backgroundColor: 'rgba(0, 0, 0, 0.08)' };
        case 'twitter':
            return { backgroundColor: 'rgba(0, 0, 0, 0.20)' };
        case 'youtube':
            return { backgroundColor: 'rgba(255, 0, 0, 0.08)' };
        case 'spotify':
            return { backgroundColor: 'rgba(25, 119, 243, 0.16)' };
        case 'apple_music':
            return { background: 'radial-gradient(122.91% 144.6% at 1.81% 90.83%, rgba(254, 213, 118, 0.16) 0%, rgba(244, 113, 51, 0.16) 26.34%, rgba(188, 48, 129, 0.16) 60.91%, rgba(76, 99, 210, 0.16) 100%)' };
        case 'amazon_music':
            return { backgroundColor: 'rgba(0, 0, 0, 0.08)' };
        case 'deezer':
            return { backgroundColor: 'rgba(0, 0, 0, 0.20)' };
        case 'tidal':
            return { backgroundColor: 'rgba(0, 0, 0, 0.20)' };
        case 'yandex':
        case 'shazam':
            return { backgroundColor: 'rgba(255, 0, 0, 0.08)' };
        case 'soundcloud':
            return { backgroundColor: 'rgba(25, 119, 243, 0.16)' };
        case 'boomplay':
            return { backgroundColor: 'rgba(25, 119, 243, 0.16)' };
        case 'anghami':
            return { backgroundColor: 'rgba(25, 119, 243, 0.16)' };
        case 'audiomack':
            return { backgroundColor: 'rgba(255, 0, 0, 0.08)' };
        case 'website':
            return { backgroundColor: '#8800ff' };
        default:
            return undefined;
    }
};

export const convertWebsiteLinkArrayToObject = (links: WebsiteLink[]) => {
    const linksObject: WebsiteLinks = {};
    links.forEach((link) => {
        linksObject[link.key] = link;
    });
    return linksObject;
}
